import { ApiSuccessSchema } from "../../common/response.schema";
import {
  DocumentTemplateCreateBodySchema,
  DocumentTemplateDeletePathVarsSchema,
  DocumentTemplateDocxToHtmlResponseSchema,
  DocumentTemplateExtractVariablesFromHtmlBodySchema,
  DocumentTemplateFormDataBodySchema,
  DocumentTemplateGenerateFileBodySchema,
  DocumentTemplateGenerateFromTemplateBodySchema,
  DocumentTemplateGenerateFromTemplateResponseSchema,
  DocumentTemplateGenerateWordFromInstructionsBodySchema,
  DocumentTemplateGetPathVarsSchema,
  DocumentTemplateListParamsSchema,
  DocumentTemplateListResponseSchema,
  DocumentTemplateParsePptxResponseSchema,
  DocumentTemplateParseWordBodySchema,
  DocumentTemplateParseWordResponseSchema,
  DocumentTemplateReindexResponseSchema,
  DocumentTemplateReindexStatusResponseSchema,
  DocumentTemplateReplaceContentResponseSchema,
  DocumentTemplateResponseSchema,
  DocumentTemplateUpdateBodySchema,
  DocumentTemplateUpdatePathVarsSchema,
  DocumentTemplateUploadFileAndAddDefaultResponseSchema,
  DocumentTemplateUploadFileResponseSchema
} from "./schemas";

/**
 * Zod API Validation Schemas for Document Templates
 *
 * Single source of truth for all document template endpoints:
 * - Defines validation rules
 * - Generates TApi types via InferSchemas
 * - Used for runtime validation (backend + frontend)
 */
export const ZDocumentTemplates = {
  // ============================================================================
  // CORE CRUD OPERATIONS
  // ============================================================================

  List: {
    method: "GET" as const,
    path: "/api/document-templates",
    Params: DocumentTemplateListParamsSchema,
    Response: ApiSuccessSchema(DocumentTemplateListResponseSchema)
  },
  Get: {
    method: "GET" as const,
    path: "/api/document-templates/:id",
    PathVars: DocumentTemplateGetPathVarsSchema,
    Response: ApiSuccessSchema(DocumentTemplateResponseSchema)
  },
  Create: {
    method: "POST" as const,
    path: "/api/document-templates",
    Body: DocumentTemplateCreateBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateResponseSchema)
  },
  Update: {
    method: "PUT" as const,
    path: "/api/document-templates/:id",
    PathVars: DocumentTemplateUpdatePathVarsSchema,
    Body: DocumentTemplateUpdateBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateResponseSchema)
  },
  Delete: {
    method: "DELETE" as const,
    path: "/api/document-templates/:id",
    PathVars: DocumentTemplateDeletePathVarsSchema,
    Response: ApiSuccessSchema(DocumentTemplateResponseSchema)
  },

  // ============================================================================
  // JSON → JSON OPERATIONS
  // ============================================================================

  ParseWord: {
    method: "POST" as const,
    path: "/api/document-templates/parse-word",
    Body: DocumentTemplateParseWordBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateParseWordResponseSchema)
  },
  ExtractVariablesFromHtml: {
    method: "POST" as const,
    path: "/api/document-templates/extract-variables-from-html",
    Body: DocumentTemplateExtractVariablesFromHtmlBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateParseWordResponseSchema)
  },
  GenerateFromTemplate: {
    method: "POST" as const,
    path: "/api/document-templates/generate-from-template",
    Body: DocumentTemplateGenerateFromTemplateBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateGenerateFromTemplateResponseSchema)
  },
  GenerateWordFromInstructions: {
    method: "POST" as const,
    path: "/api/document-templates/generate-word-from-instructions",
    Body: DocumentTemplateGenerateWordFromInstructionsBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateGenerateFromTemplateResponseSchema)
  },

  // ============================================================================
  // FORMDATA → JSON OPERATIONS
  // ============================================================================

  UploadFile: {
    method: "POST" as const,
    path: "/api/document-templates/upload-files",
    Body: DocumentTemplateFormDataBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateUploadFileResponseSchema)
  },
  UploadFileAndAddDefault: {
    method: "POST" as const,
    path: "/api/document-templates/upload-files-and-add-default",
    Body: DocumentTemplateFormDataBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateUploadFileAndAddDefaultResponseSchema)
  },
  ReplaceContentInWord: {
    method: "POST" as const,
    path: "/api/document-templates/replace-content-in-word",
    Body: DocumentTemplateFormDataBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateReplaceContentResponseSchema)
  },
  DocxToHtml: {
    method: "POST" as const,
    path: "/api/document-templates/docx-to-html",
    Body: DocumentTemplateFormDataBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateDocxToHtmlResponseSchema)
  },
  ParsePptx: {
    method: "POST" as const,
    path: "/api/document-templates/parse-pptx",
    Body: DocumentTemplateFormDataBodySchema,
    Response: ApiSuccessSchema(DocumentTemplateParsePptxResponseSchema)
  },

  // ============================================================================
  // BINARY RESPONSE (arraybuffer via @Res())
  // ============================================================================

  GenerateFile: {
    method: "POST" as const,
    path: "/api/document-templates/generate-word",
    Body: DocumentTemplateGenerateFileBodySchema,
    responseType: "blob" as const
  },

  // ============================================================================
  // ADMIN / REINDEX
  // ============================================================================

  ReindexAll: {
    method: "POST" as const,
    path: "/api/document-templates/reindex-all",
    Response: ApiSuccessSchema(DocumentTemplateReindexResponseSchema)
  },
  GetReindexStatus: {
    method: "GET" as const,
    path: "/api/document-templates/reindex-status",
    Response: ApiSuccessSchema(DocumentTemplateReindexStatusResponseSchema)
  }
} as const;
